import {
  Areas,
  Flow,
  FlowStop,
  ManualArea,
  RackingArea,
  SingularStationArea,
} from '@thive/fddm-types';
import { Vector3 } from 'three';
import { Canvas, Color } from '@thive/canvas';
import { t } from 'i18next';

import { logger } from '@common/logging/logger';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  areasToFlatCollection,
  isRackingArea,
  isSingularStationArea,
} from '@/modules/floorplanDesign/helpers';
import { isManualArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { useCanvasStore } from '@/modules/canvas/store';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { FLOW_STROKE_WIDTH } from '@/modules/canvas/constants/elements';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { formatLoadHandlingTypes } from '@/modules/floorplanDesign/helpers/singularStationAreaHelpers';

import {
  buildFlow,
  convertFlowTypeToToolFlowType,
  getFlowLineColor,
  inferFlowType,
} from '../helpers';
import {
  getPersistedFlowEndpointId,
  isFlowTypeAllowedForEndpoint,
  isShuttleFlowEndpoint,
} from '../flowEndpoint';
import {
  getValidFlowSourceLoadHandlingTypes,
  getValidFlowSourceManualLoadHandlingTypes,
  getValidFlowSourceRackingLoadHandlingTypes,
  getValidFlowTargetLoadHandlingTypes,
  getValidFlowTargetManualLoadHandlingTypes,
  getValidFlowTargetRackingLoadHandlingTypes,
} from '../consts';
import { FlowStopType, useActiveFlowStore } from '../store/useActiveFlowStore';
import { IFlowService } from './IFlowService';

type FlowToolType = 'transport_flow' | 'consolidation_flow' | 'external_flow';

export class FlowServiceImpl implements IFlowService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  private activeFlowStore: typeof useActiveFlowStore;
  private canvasSvc: ICanvasService;
  private fddmSvc: IFddmService;

  public constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
    activeFlowStore: typeof useActiveFlowStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
    this.activeFlowStore = activeFlowStore;
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
  }

  startFlow(
    pointA: Vector3,
    id: string,
    flowStopType: FlowStopType,
    loadHandlingType: SingularStationArea['loadHandlingType'],
    toolState: FlowToolType,
  ): void {
    const { design } = this.floorplanDesignStore.getState();
    const { areas, flows } = design;
    const canvas = this.getCanvas();
    const {
      setCurrentFlowSourceId,
      setCurrentFlowSourceFlowStopType,
      setCurrentFlowSourceLoadHandlingType,
      setCurrentFlowType,
    } = this.activeFlowStore.getState();
    const flowType = inferFlowType(toolState, loadHandlingType);
    const sourceIsShuttleEndpoint = isShuttleFlowEndpoint(flowStopType, id);
    if (!flowType) return;

    setCurrentFlowSourceId(id);
    setCurrentFlowSourceFlowStopType(flowStopType);
    setCurrentFlowSourceLoadHandlingType(loadHandlingType);
    setCurrentFlowType(flowType);

    // enable valid targets for flow type
    const validLoadHandlingTypes = getValidFlowTargetLoadHandlingTypes(flowType);
    const validManualLoadHandlingTypes = getValidFlowTargetManualLoadHandlingTypes(flowType);
    const validRackingLoadHandlingTypes = getValidFlowTargetRackingLoadHandlingTypes(flowType);
    const validAreas = this.getValidAreas(
      areas,
      validLoadHandlingTypes,
      validManualLoadHandlingTypes,
      validRackingLoadHandlingTypes,
      flowType,
      sourceIsShuttleEndpoint,
    );

    const validFlowStops = this.getValidFlowStops(
      flows.flowStops,
      validLoadHandlingTypes,
      flowType,
      sourceIsShuttleEndpoint,
    );

    this.canvasSvc.enableTargeting([...validAreas, ...validFlowStops]);

    const appSvc = AppServiceFactory.getInstance().createAppService();

    appSvc.setCanvasOverlay({
      message: t<string>('interface:canvas_overlay.drawing_flow_target', {
        types: formatLoadHandlingTypes(validLoadHandlingTypes),
        defaultValue: 'Select the destination area ({{types}})',
      }),
    });
    this.canvasSvc.setCursor('pointer');

    // Update tool
    canvas.updateTool('Draw.LineSticky', {
      type: 'Arrow',
      pointA,
      stroke: Color.fromHex(getFlowLineColor(flowType)),
      strokeWidth: FLOW_STROKE_WIDTH,
    });
  }

  endFlow(sourceId: string, destinationId: string): void {
    const { currentFlowType, reset: resetFlowStore } = this.activeFlowStore.getState();

    if (!currentFlowType) return;

    const persistedSourceId = getPersistedFlowEndpointId(sourceId);
    const persistedDestinationId = getPersistedFlowEndpointId(destinationId);
    if (persistedSourceId === persistedDestinationId) {
      logger.error('Cannot create flow with same source and destination', {
        sourceId,
        destinationId,
        persistedSourceId,
        persistedDestinationId,
      });
      return;
    }

    // Create new flow
    const newFlow = buildFlow(
      currentFlowType,
      persistedSourceId,
      persistedDestinationId,
      this.createDefaultFlowName(),
    );

    if (!newFlow) {
      logger.error('failed to create new flow');
      return;
    }

    this.fddmSvc.addFlows([newFlow]);
    resetFlowStore();
    this.canvasSvc.resetCursor();

    const toolFlowType = convertFlowTypeToToolFlowType(currentFlowType);

    if (!toolFlowType) return;

    this.activateTool(toolFlowType);
    this.canvasSvc.setActiveTool('cursor');
    this.canvasSvc.setActiveTool(toolFlowType);
  }

  activateTool(type: FlowToolType): void {
    const { design } = this.floorplanDesignStore.getState();
    const { areas, flows } = design;
    const flowType = this.getFlowTypeForSourceTool(type);

    const validLoadHandlingTypes = getValidFlowSourceLoadHandlingTypes(type);
    const validManualLoadHandlingTypes = getValidFlowSourceManualLoadHandlingTypes(type);
    const validRackingLoadHandlingTypes = getValidFlowSourceRackingLoadHandlingTypes(type);
    const validAreas = this.getValidAreas(
      areas,
      validLoadHandlingTypes,
      validManualLoadHandlingTypes,
      validRackingLoadHandlingTypes,
      flowType,
    );
    const validFlowStops = this.getValidFlowStops(
      flows.flowStops,
      validLoadHandlingTypes,
      flowType,
    );

    this.canvasSvc.enableTargeting([...validFlowStops, ...validAreas]);

    const appSvc = AppServiceFactory.getInstance().createAppService();

    appSvc.setCanvasOverlay({
      message: t<string>('interface:canvas_overlay.drawing_flow_source', {
        types: formatLoadHandlingTypes(validLoadHandlingTypes),
        defaultValue: 'Select the starting area ({{types}})',
      }),
    });
  }

  cancelTool(): void {
    useActiveFlowStore.getState().reset();
    this.canvasSvc.disableTargeting();
    this.canvasSvc.resetCursor();

    const appSvc = AppServiceFactory.getInstance().createAppService();

    appSvc.setCanvasOverlay(null);
  }

  //
  // Private methods
  //

  /**
   * @description
   * Private getter for the canvas instance which exposes its api.
   */
  private getCanvas = (): Canvas => {
    const canvas = this.canvasStore.getState().instance;

    if (!canvas) {
      throw new Error('Canvas instance not available.');
    }

    return canvas;
  };

  private getFlowTypeForSourceTool(type: FlowToolType): Flow['type'] | undefined {
    if (type === 'transport_flow') return 'TRANSPORT';
    if (type === 'external_flow') return 'EXTERNAL';

    return undefined;
  }

  private createDefaultFlowName(): string {
    const index = parseInt(sessionStorage.getItem('flow') ?? '1');
    sessionStorage.setItem('flow', (index + 1).toString());

    const baseCandidate = `${'Flow'} ${index}`;
    const { flows } = this.floorplanDesignStore.getState().design.flows;
    const takenNames = new Set([...flows.map((flow) => flow.name)]);

    if (!takenNames.has(baseCandidate)) return baseCandidate;

    let suffixCandidate = 1;
    let fallbackNameCandidate = `${'Flow'} ${suffixCandidate}`;

    while (takenNames.has(fallbackNameCandidate)) {
      suffixCandidate++;
      fallbackNameCandidate = `${'Flow'} ${suffixCandidate}`;
    }

    return fallbackNameCandidate;
  }

  private getValidAreas(
    areas: Areas,
    validLoadHandlingTypes: SingularStationArea['loadHandlingType'][],
    validManualLoadHandlingTypes: ManualArea['loadHandlingType'][],
    validRackingLoadHandlingTypes: RackingArea['rackingLoadHandlingType'][],
    flowType: Flow['type'] | undefined,
    sourceIsShuttleEndpoint = false,
  ): string[] {
    return areasToFlatCollection(areas)
      .filter((area) => {
        const isValidArea =
          (isSingularStationArea(area) && validLoadHandlingTypes.includes(area.loadHandlingType)) ||
          (isManualArea(area) && validManualLoadHandlingTypes.includes(area.loadHandlingType)) ||
          (isRackingArea(area) &&
            validRackingLoadHandlingTypes.includes(area.rackingLoadHandlingType));

        if (!isValidArea) return false;

        const targetIsShuttleEndpoint = isShuttleFlowEndpoint('area', area.id);

        if (sourceIsShuttleEndpoint && targetIsShuttleEndpoint) return false;
        if (!flowType) return true;

        return isFlowTypeAllowedForEndpoint(flowType, 'area', area.id);
      })
      .map((area) => area.id);
  }

  private getValidFlowStops(
    flowStops: FlowStop[],
    validLoadHandlingTypes: SingularStationArea['loadHandlingType'][],
    flowType: Flow['type'] | undefined,
    sourceIsShuttleEndpoint = false,
  ): string[] {
    return flowStops
      .filter((flowStop) => {
        const loadHandlingType = this.floorplanDesignStore
          .getState()
          .getFlowStopLoadHandlingType(flowStop.id);

        if (!loadHandlingType) return false;
        if (!validLoadHandlingTypes.includes(loadHandlingType)) return false;

        const targetIsShuttleEndpoint = isShuttleFlowEndpoint('flowStop', flowStop.id);

        if (sourceIsShuttleEndpoint && targetIsShuttleEndpoint) return false;
        if (!flowType) return true;

        return isFlowTypeAllowedForEndpoint(flowType, 'flowStop', flowStop.id);
      })
      .map((flowStop) => flowStop.id);
  }
}
